import React, { FunctionComponent, ReactNode } from 'react';
import { useAccount } from '../../hooks/useAccount';
import { useMarkStoryAsViewed } from '../../hooks/useBriefStories';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { AppSetting, BooleanSettingValue } from '../../services/models/app-settings';
import { FeatureFlag } from '../../services/models/feature-flags';
import { classNames } from '../../utils/class-names';

const useOpenInNewTab = () => {
  const { account } = useAccount();
  const settings = account?.settings || [];
  const newTabSetting = settings.find(x => x.name === AppSetting.NewTab);
  return !!newTabSetting ? newTabSetting.value === BooleanSettingValue.Yes : true;
};

interface StoryCardLinkProps {
  href: string;
  briefId: number;
  storyId: number;
  isClicked: boolean;
  children: ReactNode;
  titleClick?: (storyId: number, storyUri: string) => void;
}

const StoryCardLink: FunctionComponent<StoryCardLinkProps> = ({
  href,
  isClicked,
  briefId,
  storyId,
  children,
  titleClick,
}) => {
  const markStoryAsViewed = useMarkStoryAsViewed();

  const openInNewTab = useOpenInNewTab();

  const reportStoryClickShowStoryReaderFlag = useFeatureFlag(
    FeatureFlag.ReportStoryClickShowStoryReader
  );

  const getClassNames = () =>
    classNames(
      isClicked ? 'text-gray-400 hover:text-gray-400' : 'text-primary-deep hover:text-primary-deep'
    );

  function getLinkComponent(openInNewTab: boolean) {
    if (!reportStoryClickShowStoryReaderFlag || !titleClick) {
      if (!openInNewTab) {
        // Same-tab clicks now fire the click mutation too (the legacy
        // handler forgot to dispatch).
        return (
          <a
            onClick={() => markStoryAsViewed(storyId, briefId)}
            className={getClassNames()}
            href={href}
          >
            {children}
          </a>
        );
      } else {
        return (
          <a
            href={href}
            onClick={() => markStoryAsViewed(storyId, briefId)}
            className={getClassNames()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }
    }

    const ref = '#';

    return (
      <a
        href={ref}
        onClick={() => {
          if (titleClick) {
            titleClick(storyId, href);
          }

          markStoryAsViewed(storyId, briefId);
        }}
        className={getClassNames()}
      >
        {children}
      </a>
    );
  }

  return getLinkComponent(openInNewTab);
};

export default StoryCardLink;
